import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import { Button, DatePicker, message, Select, Table, Tag, Tooltip } from "antd";
import { Doughnut, Bar, Line, getElementAtEvent } from "react-chartjs-2";
import GlobalDiamondReportService from "../../services/reports-globalDiamond-service";
import ReportJewelryReportService from "../../services/reports-Jewelry-service";
import ReportsDiamondService from "../../services/reports-Diamond.service";
import { chartColors } from "./colors";
import { bucketTopN, toShareRows } from "../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { ACCENT, GRID_COLOR, donutOptions, barOptions, donutPctOptions, barPctOptions } from "../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { trendFor, pctTotalKpi } from "../ChartsGroup/DiamondReportsCharts/_shared/kpiHelpers";
import { BlobToDownloadFile } from "../../helper/commanFunction";

const { RangePicker } = DatePicker;

// Vendor based Add To Cart / Wish List report (Jewelry + Diamond).
// Data: ActivityTrackingJewelry / ActivityTrackingDiamond via the Reports API ActivityTracking endpoints.
// Same layout/charts as the standard Jewelry Chart Report (JeweleryChartReports.js):
// Trend (line), Category/Shape (donut), Price (vertical bars), Vendor (horizontal bars).
// Clicking Category/Shape, Price or Vendor filters the table. The PDF has the same 4 charts.
const COLUMNS = {
  Jewelry: [
    { title: "User IP Address", dataIndex: "UserIPAddress", sortKey: "UserIPAddress" },
    { title: "Date", dataIndex: "Date", sortKey: "Date", date: true },
    { title: "Vendor", dataIndex: "VendorName", sortKey: "VendorName" },
    { title: "Retailer", dataIndex: "RetailerName", sortKey: "RetailerName" },
    { title: "SKU", dataIndex: "ProductId", sortKey: "ProductId" },
    { title: "Style Number", dataIndex: "StyleNumber", sortKey: "StyleNumber" },
    { title: "Category", dataIndex: "Category", sortKey: "Category" },
    { title: "Metal Type", dataIndex: "MetalType", sortKey: "MetalType" },
    { title: "Metal Color", dataIndex: "MetalColor", sortKey: "MetalColor" },
    { title: "Price", dataIndex: "Price", sortKey: "Price" },
  ],
  Diamond: [
    { title: "User IP Address", dataIndex: "UserIPAddress", sortKey: "UserIPAddress" },
    { title: "Date", dataIndex: "Date", sortKey: "Date", date: true },
    { title: "Vendor", dataIndex: "VendorName", sortKey: "VendorName" },
    { title: "Retailer", dataIndex: "RetailerName", sortKey: "RetailerName" },
    { title: "Stock No", dataIndex: "DealerStockNo", sortKey: "DealerStockNo" },
    { title: "Shape", dataIndex: "Shape", sortKey: "Shape" },
    { title: "Carat", dataIndex: "Carat", sortKey: "Carat" },
    { title: "Color", dataIndex: "Color", sortKey: "Color" },
    { title: "Clarity", dataIndex: "Clarity", sortKey: "Clarity" },
    { title: "Cut Grade", dataIndex: "CutGrade", sortKey: "CutGrade" },
    { title: "Certificate", dataIndex: "Certificate", sortKey: "Certificate" },
    { title: "Type", dataIndex: "DiamondType" },
    { title: "Price", dataIndex: "Price", sortKey: "Price" },
  ],
};

const formatDate = (value) => (value ? moment(value).format("MM-DD-YYYY, hh:mm A") : "");
// Same as JeweleryChartReports.js - price bands in natural order, "Call For Price" last
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};
const stripPct = (label) => (label || "").replace(/\s*\([^)]*\)\s*$/, "");
const emptyFilter = { vendor: null, retailer: null, typeName: "", priceBand: "" };
const toText = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

// percentOnly (Reports > Global Jewelry Add to Cart / Wish List, Task 2344): every
// figure is shown as a share (%), never a raw count.
// priorLastYear (same pages): "vs prior period" compares with the same dates last year.
const VendorActivityReport = ({ productType, addToType, title, isGlobal = false, percentOnly = false, priorLastYear = false, hideFilters = false }) => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const service = productType === "Diamond" ? ReportsDiamondService : ReportJewelryReportService;
  const columnsConfig = COLUMNS[productType];

  const [dateRange, setDateRange] = useState({
    from: moment().subtract(30, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });
  // Range the data on screen was loaded for (changes only on Apply Filter) - used by
  // paging/sorting/vendor filter/PDF so they never mix in an un-applied picker value.
  const [appliedRange, setAppliedRange] = useState(dateRange);
  // Dropdown (#1713 case 4): connected vendors for a retailer login, connected retailers for a
  // vendor login (none for Admin). Applied with Apply Filter, like the date range.
  const [filterType, setFilterType] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [filterIds, setFilterIds] = useState([]);
  const [appliedFilterIds, setAppliedFilterIds] = useState([]);
  // Global pages (#1713 case 6): all data, two dropdowns - filterIds/filterOptions hold the
  // Vendor(s) one, these the Retailer(s) one
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [retailerFilterIds, setRetailerFilterIds] = useState([]);
  const [appliedRetailerFilterIds, setAppliedRetailerFilterIds] = useState([]);
  // Vendor(s) + Retailer(s) dropdowns and Top Vendor + Top Retailer: global pages, and an Admin
  // login on an own-account page (API returns FilterType "Admin" - the admin account's own vendors)
  const globalMode = isGlobal || filterType === "Admin";
  const [dealerId, setDealerId] = useState(null);
  const [forDealer, setForDealer] = useState(2);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [trendList, setTrendList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [priorTotal, setPriorTotal] = useState(0);
  const [chartLoading, setChartLoading] = useState(false);
  // table filters set by clicking the Category/Shape, Price and Vendor charts
  const [filter, setFilter] = useState(emptyFilter);
  const typeChartRef = useRef();
  const priceChartRef = useRef();
  const vendorChartRef = useRef();
  const [tableList, setTableList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortColumnName, setSortColumnName] = useState("Date");
  const [sortType, setSortType] = useState("DESC");
  // Table search box - searched server side across every page (same as the other report tables)
  const [searchQuery, setSearchQuery] = useState("");
  const isFirstSearchRender = useRef(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [tableCsvLoading, setTableCsvLoading] = useState(false);
  const [tablePdfLoading, setTablePdfLoading] = useState(false);

  const totalActivity = vendorList.reduce((sum, item) => sum + (item.CNT || 0), 0);
  const activityLabel = addToType === "AddToCart" ? t("Add To Cart") : t("Wish List");
  const typeLabel = productType === "Diamond" ? t("Shape") : t("Category");

  useEffect(() => {
    const responseData = loginDetials?.loginDetials?.responseData;
    if (responseData?.logInSucceeded) {
      setDealerId(responseData.dealerId);
      handleCheckDealermembers(responseData.dealerId);
    }
  }, []);

  const buildRequest = (overrides = {}) => ({
    dealerID: dealerId,
    forDealer: forDealer,
    fromDate: appliedRange.from,
    toDate: appliedRange.to,
    addToType: addToType,
    filterIds: globalMode ? "" : appliedFilterIds.join(","),
    isGlobal: isGlobal,
    percentOnly: percentOnly,
    priorLastYear: priorLastYear,
    hideFilters: hideFilters,
    vendorFilterIds: globalMode ? appliedFilterIds.join(",") : "",
    retailerFilterIds: globalMode ? appliedRetailerFilterIds.join(",") : "",
    vendorID: filter.vendor ? filter.vendor.VendorID : null,
    vendorName: filter.vendor ? filter.vendor.VendorName : "",
    retailerID: filter.retailer ? Number(filter.retailer.RetailerID) : null,
    typeName: filter.typeName,
    priceBand: filter.priceBand,
    pageIndex: pageNo,
    pageSize: pageSize,
    sortColumnName: sortColumnName,
    sortOrder: sortType,
    searchText: searchQuery.trim(),
    ...overrides,
  });

  /*API Binding Starts*/
  const handleCheckDealermembers = (id) => {
    GlobalDiamondReportService.CheckDealermembers({ dealerID: id })
      .then((response) => {
        const value =
          response.data.message === "Success" ? response.data.responseData?.forDealer || 2 : 2;
        setForDealer(value);
        handleLoadFilterList(id);
        const request = buildRequest({ dealerID: id, forDealer: value });
        handleLoadChart(request);
        handleLoadTable(request);
      })
      .catch(() => {
        const request = buildRequest({ dealerID: id, forDealer: 2 });
        handleLoadChart(request);
        handleLoadTable(request);
      });
  };

  const handleLoadFilterList = (id) => {
    service
      .ActivityTrackingFilterList({ dealerID: id, fromDate: appliedRange.from, toDate: appliedRange.to, addToType, isGlobal })
      .then((response) => {
        const data = response.data.message === "Success" ? response.data.responseData || {} : {};
        setFilterType(data.FilterType || "");
        setFilterOptions((data.VendorOptions || data.Options) || []);
        setRetailerOptions(data.RetailerOptions || []);
      })
      .catch(() => {
        setFilterType("");
        setFilterOptions([]);
      });
  };

  const handleLoadChart = (request) => {
    setChartLoading(true);
    service
      .ActivityTrackingChartData(request)
      .then((response) => {
        setChartLoading(false);
        const data = response.data.message === "Success" ? response.data.responseData || {} : {};
        setVendorList(data.VendorBasedData || []);
        setRetailerList(data.RetailerBasedData || []);
        setTrendList(data.TrendData || []);
        setTypeList(data.TypeData || []);
        setPriceList(data.PriceData || []);
        setPriorTotal(Number(data.PriorTotal) || 0);
      })
      .catch(() => {
        setChartLoading(false);
        setVendorList([]);
        setRetailerList([]);
        setTrendList([]);
        setTypeList([]);
        setPriceList([]);
        setPriorTotal(0);
      });
  };

  // Debounced search - reloads the table from page 1 (charts are not affected)
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    if (!dealerId) return;
    const handle = setTimeout(() => {
      setPageNo(1);
      handleLoadTable(buildRequest({ pageIndex: 1, searchText: searchQuery.trim() }));
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleLoadTable = (request) => {
    setTableLoading(true);
    service
      .ActivityTrackingTableData(request)
      .then((response) => {
        setTableLoading(false);
        if (response.data.message === "Success") {
          setTableList(response.data.responseData?.TableData || []);
          setTotalCount(response.data.responseData?.TotalRecords || 0);
        } else {
          setTableList([]);
          setTotalCount(0);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setTableList([]);
        setTotalCount(0);
      });
  };
  /*API Binding Ends*/

  const handleApplyFilter = () => {
    setPageNo(1);
    setFilter(emptyFilter);
    setAppliedRange(dateRange);
    setAppliedFilterIds(filterIds);
    setAppliedRetailerFilterIds(retailerFilterIds);
    const request = buildRequest({
      pageIndex: 1, vendorID: null, vendorName: "", retailerID: null, typeName: "", priceBand: "", fromDate: dateRange.from, toDate: dateRange.to,
      filterIds: globalMode ? "" : filterIds.join(","),
      vendorFilterIds: globalMode ? filterIds.join(",") : "",
      retailerFilterIds: globalMode ? retailerFilterIds.join(",") : "",
    });
    handleLoadChart(request);
    handleLoadTable(request);
  };

  const applyTableFilter = (changes) => {
    const next = { ...filter, ...changes };
    setFilter(next);
    setPageNo(1);
    handleLoadTable(
      buildRequest({
        pageIndex: 1,
        vendorID: next.vendor ? next.vendor.VendorID : null,
        vendorName: next.vendor ? next.vendor.VendorName : "",
        retailerID: next.retailer ? Number(next.retailer.RetailerID) : null,
        typeName: next.typeName,
        priceBand: next.priceBand,
      })
    );
  };

  // Same click handling as JeweleryChartReports.js (label without the "(xx%)" suffix)
  const makeClickHandler = (ref, rows, onPick) => (event) => {
    if (!ref.current) return;
    const [el] = getElementAtEvent(ref.current, event);
    if (!el) return;
    const label = stripPct(rows[el.index]?.[0]);
    if (label && label !== t("Others")) onPick(label);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const tableBody = document.querySelector(".pendjewelreq__tbl .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;
    const column = sorter.order ? sorter.column?.sortKey || "Date" : "Date";
    const type = sorter.order === "ascend" ? "ASC" : "DESC";
    setSortColumnName(column);
    setSortType(type);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    handleLoadTable(
      buildRequest({
        pageIndex: pagination.current,
        pageSize: pagination.pageSize,
        sortColumnName: column,
        sortOrder: type,
      })
    );
  };

  // Download PDF - same server-rendered report PDF as the other report pages
  const handleExportToPDF = () => {
    setPdfLoading(true);
    service
      .ActivityTrackingExportPdf(buildRequest({ pageIndex: 1 }))
      .then((response) => {
        setPdfLoading(false);
        const type = addToType === "AddToCart" ? "AddToCart" : "WishList";
        BlobToDownloadFile(response.data, `${productType}_Vendor_${type}_${appliedRange.from}_${appliedRange.to}.pdf`, "pdf");
      })
      .catch(() => {
        setPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  // Table "CSV" / "PDF" buttons - all rows matching the current filters and sort (same as the other report tables)
  const handleTableExport = (kind) => {
    const setLoading = kind === "csv" ? setTableCsvLoading : setTablePdfLoading;
    const call = kind === "csv" ? service.ActivityTrackingTableCsv : service.ActivityTrackingTablePdf;
    setLoading(true);
    call
      .call(service, buildRequest({ pageIndex: 1 }))
      .then((response) => {
        setLoading(false);
        const type = addToType === "AddToCart" ? "AddToCart" : "WishList";
        BlobToDownloadFile(response.data, `${productType}_${type}_Log_${appliedRange.from}_${appliedRange.to}.${kind}`, kind === "pdf" ? "pdf" : undefined);
      })
      .catch(() => {
        setLoading(false);
        message.error(kind === "csv" ? t("Failed to generate CSV. Please try again.") : t("Failed to generate PDF. Please try again."));
      });
  };

  // Chart data - same helpers as JeweleryChartReports.js
  const typeChart = useMemo(
    () => bucketTopN(typeList, { labelKey: "Name", valueKey: "CNT", chartLabel: typeLabel, othersLabel: t("Others") }),
    [typeList, typeLabel, t]
  );
  // 4th chart: by Retailer for a vendor login (its own data is all one vendor), otherwise by Vendor
  const chartByRetailer = !globalMode && filterType === "Retailer";
  const vendorChart = useMemo(
    () =>
      chartByRetailer
        ? bucketTopN(retailerList, { labelKey: "RetailerName", valueKey: "CNT", chartLabel: "Retailer", othersLabel: t("Others") })
        : bucketTopN(vendorList, { labelKey: "VendorName", valueKey: "CNT", chartLabel: "Vendor", othersLabel: t("Others") }),
    [chartByRetailer, retailerList, vendorList, t]
  );
  const priceRows = useMemo(() => {
    const list = priceList.filter((item) => Number(item.CNT) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.Range) - bandStart(b.Range));
    const total = sorted.reduce((sum, item) => sum + Number(item.CNT), 0);
    return sorted.map((item) => ({
      label: item.Range,
      value: Number(item.CNT),
      percentage: total > 0 ? ((Number(item.CNT) / total) * 100).toFixed(2) : 0,
    }));
  }, [priceList]);
  const priceChartData = [["Price Band", "Count"], ...priceRows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];
  const hasTrend = trendList.some((item) => Number(item.CNT) > 0);

  // Same as DiamondReportsClicksComp.js (line view)
  const trendData = {
    labels: trendList.map((item) => item.Date),
    datasets: [
      {
        label: activityLabel,
        data: percentOnly ? toShareRows(trendList.map((item) => [item.Date, item.CNT])).map((r) => r[1]) : trendList.map((item) => Number(item.CNT)),
        backgroundColor: "rgba(59, 91, 219, .15)",
        borderColor: ACCENT,
        borderWidth: 2,
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };
  const trendOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: { legend: { display: false }, ...(percentOnly && { tooltip: { callbacks: { label: (ctx) => ` ${Number(ctx.raw).toFixed(2)}%` } } }) },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: GRID_COLOR }, beginAtZero: true, ticks: percentOnly ? { callback: (v) => `${v}%` } : { precision: 0 } },
    },
  };

  const columns = columnsConfig.map((c) => ({
    title: <Tooltip title={t(c.title)}>{t(c.title)}</Tooltip>,
    dataIndex: c.dataIndex,
    sortKey: c.sortKey,
    ellipsis: true,
    sorter: c.sortKey ? true : false,
    defaultSortOrder: c.sortKey === "Date" ? "descend" : undefined,
    render: (value) => {
      const text = c.date ? formatDate(value) : toText(value);
      return (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      );
    },
  }));

  // vendor login (dropdown lists retailers) -> "Top Retailer", otherwise "Top Vendor";
  // global page -> "Top Vendor" and an extra "Top Retailer" card
  const isVendorLogin = !globalMode && filterType === "Retailer";
  const topRetailer = globalMode && retailerList[0] ? { name: retailerList[0].RetailerName, cnt: retailerList[0].CNT } : null;
  const topPartner = isVendorLogin
    ? retailerList[0] && { name: retailerList[0].RetailerName, cnt: retailerList[0].CNT }
    : vendorList[0] && { name: vendorList[0].VendorName, cnt: vendorList[0].CNT };
  const topType = [...typeList].sort((a, b) => Number(b.CNT) - Number(a.CNT))[0];
  const topPriceBand = [...priceRows].sort((a, b) => b.value - a.value)[0];
  const activityWord = addToType === "AddToCart" ? t("add to cart") : t("wish list adds");
  const totalTrend = trendFor([{ Metric: "Total", CurrentValue: totalActivity, PriorValue: priorTotal }], "Total");
  const totalPct = pctTotalKpi(totalActivity, priorTotal);
  const shareOf = (value, list) => {
    const total = list.reduce((sum, item) => sum + Number(item.CNT || 0), 0);
    return total > 0 ? ((Number(value || 0) / total) * 100).toFixed(2) : "0.00";
  };
  // KPI sub line: "N add to cart", or "xx.xx% of add to cart" on percent-only pages
  const kpiSub = (value, list) => (percentOnly ? `${shareOf(value, list)}% ${t("of")} ${activityWord}` : `${Number(value).toLocaleString()} ${activityWord}`);
  const chartValues = (rows) => (percentOnly ? toShareRows(rows) : rows).map((r) => r[1]);
  const periodLabel = `${moment(appliedRange.from).format("MMM D")} - ${moment(appliedRange.to).format("MMM D, YYYY")}`;

  return (
    <div className="pendjewelreq__section form__fields">
      <div className="row magento__mapping">
        <div className="col-md-12">
          <div className="edr-pagehead-row">
            <div className="edr-pagehead">
              <h1 className="edr-pagehead-title">{t(title)}</h1>
              <div className="edr-pagehead-sub">
                {moment(appliedRange.from).format("MMM D, YYYY")} – {moment(appliedRange.to).format("MMM D, YYYY")}
              </div>
            </div>
            <div className="edr-pagehead-actions">
              <Button className="ghost-btn" onClick={handleExportToPDF} loading={pdfLoading}>
                <i className="fa fa-download"></i> {t("Download PDF")}
              </Button>
              <button className="primary-btn" onClick={handleApplyFilter}>{t("Apply Filter")}</button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="row edr-filters">
            <div className="col-md-3">
              <div className="input__block dataRange-input-block datepicker__block">
                <label>{t("Date Range")}</label>
                <RangePicker
                  className="date-range-input"
                  ranges={{
                    [t("Today")]: [moment(), moment()],
                    [t("Yesterday")]: [moment().subtract(1, "days"), moment().subtract(1, "days")],
                    [t("Last 7 Days")]: [moment().subtract(7, "days"), moment()],
                    [t("Last 30 Days")]: [moment().subtract(30, "days"), moment()],
                    [t("This Month")]: [moment().startOf("month"), moment().endOf("month")],
                    [t("Last Month")]: [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "months").endOf("month")],
                    [t("Last 90 Days")]: [moment().subtract(90, "days"), moment()],
                    [t("Year To Date")]: [moment().startOf("year"), moment()],
                  }}
                  format={"MM-DD-YYYY"}
                  onChange={(dates) => {
                    if (dates) {
                      setDateRange({ from: dates[0].format("YYYY-MM-DD"), to: dates[1].format("YYYY-MM-DD") });
                    }
                  }}
                  defaultValue={[moment().subtract(30, "days"), moment()]}
                  defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                />
              </div>
            </div>
            {/* #2347: Global Diamond / Global Jewelry pages show no Vendor(s) / Retailer(s) dropdowns */}
            {globalMode && !hideFilters && (
              <>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Vendor(s)")}</label>
                    <Select
                      className="border__grey"
                      showSearch
                      showArrow
                      mode="multiple"
                      placeholder={t("Select One or More Vendors")}
                      optionFilterProp="children"
                      value={filterIds}
                      onChange={setFilterIds}
                    >
                      {filterOptions.map((item) => (
                        <Select.Option key={`vendor-${item.DealerID}`} value={String(item.DealerID)}>
                          {item.DealerCompany}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Retailer(s)")}</label>
                    <Select
                      className="border__grey"
                      showSearch
                      showArrow
                      mode="multiple"
                      placeholder={t("Select one or more retailer")}
                      optionFilterProp="children"
                      value={retailerFilterIds}
                      onChange={setRetailerFilterIds}
                    >
                      {retailerOptions.map((item) => (
                        <Select.Option key={`retailer-${item.DealerID}`} value={String(item.DealerID)}>
                          {item.DealerCompany}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </>
            )}
            {filterType && !globalMode && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{filterType === "Vendor" ? t("Vendor(s)") : t("Retailer(s)")}</label>
                  <Select
                    className="border__grey"
                    showSearch
                    showArrow
                    mode="multiple"
                    placeholder={filterType === "Vendor" ? t("Select One or More Vendors") : t("Select one or more retailer")}
                    optionFilterProp="children"
                    value={filterIds}
                    onChange={setFilterIds}
                  >
                    {filterOptions.map((item) => (
                      <Select.Option key={`filter-${item.DealerID}`} value={String(item.DealerID)}>
                        {item.DealerCompany}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-kpis">
            {percentOnly ? (
              // Percent-only (Task 2344): the total would always be 100%, so
              // the card shows the change vs the prior period instead.
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Total")} {activityLabel}</div>
                <div className="edr-kpi-val">{chartLoading ? "…" : totalPct.val}</div>
              </div>
            ) : (
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Total")} {activityLabel}</div>
              <div className="edr-kpi-val">{chartLoading ? "—" : totalActivity.toLocaleString()}</div>
              {!chartLoading && totalTrend && (
                <div className={`edr-kpi-trend ${totalTrend.up ? "edr-up" : "edr-down"}`}>
                  {totalTrend.up ? "▲" : "▼"} {Math.abs(totalTrend.change).toFixed(2)}% {t("vs prior period")}
                </div>
              )}
            </div>
            )}
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top")} {typeLabel}</div>
              <div className="edr-kpi-val">{chartLoading ? "—" : topType?.Name ?? "—"}</div>
              {!chartLoading && topType && <div className="edr-kpi-sub">{kpiSub(topType.CNT, typeList)}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Price Sweet Spot")}</div>
              <div className="edr-kpi-val">{chartLoading ? "—" : topPriceBand?.label ?? "—"}</div>
              {!chartLoading && topPriceBand && <div className="edr-kpi-sub">{percentOnly ? `${topPriceBand.percentage}% ${t("of")} ${activityWord}` : `${topPriceBand.value.toLocaleString()} ${activityWord}`}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{isVendorLogin ? t("Top Retailer") : t("Top Vendor")}</div>
              <div className="edr-kpi-val">{chartLoading ? "—" : topPartner?.name ?? "—"}</div>
              {!chartLoading && topPartner && <div className="edr-kpi-sub">{kpiSub(topPartner.cnt, isVendorLogin ? retailerList : vendorList)}</div>}
            </div>
            {globalMode && (
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Top Retailer")}</div>
                <div className="edr-kpi-val">{chartLoading ? "—" : topRetailer?.name ?? "—"}</div>
                {!chartLoading && topRetailer && <div className="edr-kpi-sub">{kpiSub(topRetailer.cnt, retailerList)}</div>}
              </div>
            )}
          </div>
        </div>

        {!chartLoading && (
          <div className="col-md-12">
            <div className="edr-insight">
              {/* same sentence as the PDF insight banner */}
              💡{" "}
              {topType ? (
                <>
                  {activityLabel} {t("profile this period: shoppers gravitate to")} <b>{topType.Name}</b>
                  {topPriceBand && <> {t("in the")} <b>{topPriceBand.label}</b> {t("band")}</>}
                  {topPartner && <>, {t("with")} <b>{topPartner.name}</b> {isVendorLogin ? t("the standout retailer") : t("the standout vendor")}</>}
                  {topRetailer && <> {t("and")} <b>{topRetailer.name}</b> {t("the standout retailer")}</>}.
                </>
              ) : (
                <>{t("No")} {activityLabel.toLowerCase()} {t("activity recorded for")} {periodLabel}.</>
              )}
            </div>
          </div>
        )}

        <div className="col-md-12">
          <div className="row">
            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{activityLabel} {t("Trend")}</b></label>
                </div>
                <div className="chart__section">
                  {!chartLoading && hasTrend ? (
                    <div className="edr-chartwrap edr-tall">
                      <Line data={trendData} options={trendOptions} />
                    </div>
                  ) : (
                    <ChartState loading={chartLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{activityLabel} {t("by")} {typeLabel}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartLoading && typeChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={typeChart.chartData} colorData={typeChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={typeChartRef}
                          data={{
                            labels: typeChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: chartValues(typeChart.chartData.slice(1)), backgroundColor: typeChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={percentOnly ? donutPctOptions : donutOptions}
                          onClick={makeClickHandler(typeChartRef, typeChart.chartData.slice(1), (label) => applyTableFilter({ typeName: label }))}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{activityLabel} {t("by Price")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartLoading && priceRows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={priceChartRef}
                        data={{
                          labels: priceChartData.slice(1).map((r) => r[0]),
                          // one color per price band, same palette/order as the PDF
                          datasets: [{ data: chartValues(priceChartData.slice(1)), backgroundColor: priceRows.map((_, i) => chartColors[i % chartColors.length]), borderRadius: 4 }],
                        }}
                        options={percentOnly ? barPctOptions(false) : barOptions(false)}
                        onClick={makeClickHandler(priceChartRef, priceChartData.slice(1), (label) => applyTableFilter({ priceBand: label }))}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{activityLabel} {chartByRetailer ? t("by Retailer") : t("by Vendor")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartLoading && vendorChart.chartData.length > 1 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={vendorChartRef}
                        data={{
                          labels: vendorChart.chartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: chartValues(vendorChart.chartData.slice(1)), backgroundColor: vendorChart.colors, borderRadius: 4 }],
                        }}
                        options={percentOnly ? barPctOptions(true) : barOptions(true)}
                        onClick={makeClickHandler(vendorChartRef, vendorChart.chartData.slice(1), (label) =>
                          chartByRetailer
                            ? applyTableFilter({ retailer: retailerList.find((r) => r.RetailerName === label) || null })
                            : applyTableFilter({ vendor: vendorList.find((v) => v.VendorName === label) || null })
                        )}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartLoading} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-panel mt-4">
            <div className="edr-panel-head">
              <div>
                <h3>{activityLabel} {t("Log")}</h3>
                <div className="edr-panel-desc">{t("Individual activity records · click a chart to filter, a column to sort")}</div>
              </div>
              <div className="edr-pagehead-actions">
                <Button className="ghost-btn" onClick={() => handleTableExport("csv")} loading={tableCsvLoading}>
                  <i className="fa fa-download"></i> {t("CSV")}
                </Button>
                <Button className="ghost-btn" onClick={() => handleTableExport("pdf")} loading={tablePdfLoading}>
                  <i className="fa fa-download"></i> {t("PDF")}
                </Button>
              </div>
            </div>

            <div className="edr-tblbar">
              <input
                className="edr-tblsearch"
                placeholder={productType === "Diamond" ? t("Search vendor, retailer, stock no, shape…") : t("Search vendor, retailer, SKU, category…")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {filter.typeName && (
                <Tag color="blue" closable onClose={() => applyTableFilter({ typeName: "" })}>
                  {typeLabel}: {filter.typeName}
                </Tag>
              )}
              {filter.priceBand && (
                <Tag color="blue" closable onClose={() => applyTableFilter({ priceBand: "" })}>
                  {t("Price")}: {filter.priceBand}
                </Tag>
              )}
              {filter.vendor && (
                <Tag color="blue" closable onClose={() => applyTableFilter({ vendor: null })}>
                  {t("Vendor")}: {filter.vendor.VendorName}
                </Tag>
              )}
              {filter.retailer && (
                <Tag color="blue" closable onClose={() => applyTableFilter({ retailer: null })}>
                  {t("Retailer")}: {filter.retailer.RetailerName}
                </Tag>
              )}
              {!percentOnly && <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>}
            </div>

            <div className="edr-detail-table">
              <Table
                className="pendjewelreq__tbl"
                scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
                loading={tableLoading}
                locale={{ emptyText: t("No Data") }}
                columns={columns}
                dataSource={tableList}
                rowKey="Id"
                pagination={{
                  size: "small",
                  total: totalCount,
                  current: pageNo,
                  pageSize: pageSize,
                  pageSizeOptions: [10, 15, 20, 50, 100],
                  responsive: true,
                  showSizeChanger: true,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorActivityReport;
